import { ColumnFiltersState } from '@tanstack/react-table'
import { attempt } from 'lodash'

import { IAddress } from '~/types'
import { Address } from '~/utils/address'
import { unwrapToken } from '~/utils/tokens'

export type DirectionAndValueFilterValue = {
  min: string
  max: string
  direction: {
    inbound: boolean
    outbound: boolean
  }
}

export const parseDirectionAndValue = (
  value: unknown,
): DirectionAndValueFilterValue | Error => {
  return attempt(
    () => JSON.parse(value as string) as DirectionAndValueFilterValue,
  )
}

export const COLUMN_IDS = {
  MEMO: 'memo',
  TRANSACTION_HASH: 'transactionHash',
  USD_VALUE: 'usdValue',
  SMART_ACCOUNT_ID: 'smartAccountId',
  CHAIN_ID: 'chainId',
  TOKEN: 'token',
} as const

// Token filter keys are `${chainId}:${address}` — lowercased, natives
// unwrapped to the zero address — so keys from balances, the URL, and the
// tokens tab all match. The server re-checksums before querying.
export const buildTokenFilterKey = ({
  chainId,
  address,
}: {
  chainId: number
  address: string
}): string => `${chainId}:${unwrapToken(address as IAddress).toLowerCase()}`

export const parseTokenFilterKey = (
  key: string,
): { chainId: number; address: string } | null => {
  const separatorIndex = key.indexOf(':')
  if (separatorIndex === -1) return null
  const chainId = Number(key.slice(0, separatorIndex))
  const address = key.slice(separatorIndex + 1)
  // Keys are hand-editable via the URL; a junk address throws in
  // Address.format downstream.
  if (
    !Number.isInteger(chainId) ||
    chainId <= 0 ||
    !Address.validate(address, 'evm')
  ) {
    return null
  }
  return { chainId, address }
}

export const DEFAULT_VALUE_FILTER: DirectionAndValueFilterValue = {
  min: '',
  max: '',
  direction: { inbound: true, outbound: true },
}

export type Direction = 'any' | 'in' | 'out'

export const directionFromFilter = (
  filter: DirectionAndValueFilterValue,
): Direction => {
  if (filter.direction.inbound && filter.direction.outbound) return 'any'
  if (filter.direction.inbound) return 'in'
  return 'out'
}

export const directionToFilter = (
  dir: Direction,
): DirectionAndValueFilterValue['direction'] => {
  switch (dir) {
    case 'any':
      return { inbound: true, outbound: true }
    case 'in':
      return { inbound: true, outbound: false }
    case 'out':
      return { inbound: false, outbound: true }
  }
}

export const isValueFilterEmpty = (f: DirectionAndValueFilterValue) =>
  f.min === '' && f.max === '' && f.direction.inbound && f.direction.outbound

export const getColumnFilterValue = (
  columnFilters: ColumnFiltersState,
  id: string,
): string => {
  const filter = columnFilters.find((f) => f.id === id)
  return (filter?.value as string) ?? ''
}

export const setColumnFilterValue = (
  columnFilters: ColumnFiltersState,
  id: string,
  value: string,
): ColumnFiltersState => {
  const exists = columnFilters.some((f) => f.id === id)
  if (exists) {
    return value === ''
      ? columnFilters.filter((f) => f.id !== id)
      : columnFilters.map((f) => (f.id === id ? { ...f, value } : f))
  }
  return value === '' ? columnFilters : [...columnFilters, { id, value }]
}

// Chip label for a USD min/max range: "$1,000-$2,000", ">$1,000", "<$2,000".
// Bounds are hand-editable via the URL, so anything unparseable is shown as
// typed rather than as NaN.
export const usdRangeLabel = ({
  min,
  max,
}: {
  min: string
  max: string
}): string => {
  const format = (value: string) => {
    const num = Number(value)
    return `$${Number.isFinite(num) ? num.toLocaleString('en-US') : value}`
  }
  if (min !== '' && max !== '') return `${format(min)}-${format(max)}`
  if (min !== '') return `>${format(min)}`
  if (max !== '') return `<${format(max)}`
  return ''
}
